import React, { useEffect } from 'react';

// Public account & data deletion page, reachable at /delete-account without
// signing in. Required to be publicly accessible for Google Play's data-safety
// section. Styled to match the public privacy page: dark slate background with
// the Cormorant Garamond serif and the gold wordmark. The deletion STEPS are the
// deliberate focus of the page (a highlighted card above the explanatory text).
export default function DeleteAccount() {
  useEffect(() => {
    document.title = 'Delete Your Account | Narratoria';
    const id = 'cormorant-garamond-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  // Gold wordmark, matching the landing page without using amber- classes.
  const gold = {
    backgroundImage: 'linear-gradient(to bottom, #fde9b8, #d9a54b, #b07d2b)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };

  // Pre-fill the subject line so the request arrives correctly labelled.
  const mailto = 'mailto:admin@narratoria.com?subject=Delete%20my%20account';

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-950 via-[#0d1117] to-slate-950 text-slate-200"
      style={serif}
    >
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
        <header className="mb-10 text-center">
          <a
            href="/reader"
            className="text-4xl sm:text-5xl font-semibold tracking-wide"
            style={{ ...serif, ...gold }}
          >
            NARRATORIA
          </a>
          <h1 className="mt-6 text-3xl sm:text-4xl font-semibold text-white">
            Delete your account and data
          </h1>
          <p className="mt-2 text-slate-400">
            How to permanently delete your Narratoria account and the data we hold about you.
          </p>
        </header>

        {/* ── The focus of the page: how to request deletion ── */}
        <section className="rounded-xl border border-teal-500/40 bg-slate-900/60 p-6 shadow-lg sm:p-8">
          <h2 className="text-2xl font-semibold text-white">How to request deletion</h2>
          <p className="mt-2 leading-relaxed text-slate-300">
            To delete your Narratoria account and the data linked to it, send us one
            email. There is no form to fill in — just follow these steps:
          </p>

          <ol className="mt-6 space-y-5">
            <li className="flex gap-4">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-teal-600 text-sm font-semibold text-white">
                1
              </span>
              <p className="leading-relaxed text-slate-200">
                <span className="font-semibold text-white">Address your email to{' '}
                <a className="text-teal-400 hover:text-teal-300" href={mailto}>
                  admin@narratoria.com
                </a>.</span>
              </p>
            </li>
            <li className="flex gap-4">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-teal-600 text-sm font-semibold text-white">
                2
              </span>
              <p className="leading-relaxed text-slate-200">
                <span className="font-semibold text-white">Use the subject line{' '}
                &ldquo;Delete my account&rdquo;.</span>
              </p>
            </li>
            <li className="flex gap-4">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-teal-600 text-sm font-semibold text-white">
                3
              </span>
              <p className="leading-relaxed text-slate-200">
                <span className="font-semibold text-white">Send it from the email address
                on your account</span> so we can confirm it is you, and tell us whether you
                want your whole account deleted or only specific data removed.
              </p>
            </li>
            <li className="flex gap-4">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-teal-600 text-sm font-semibold text-white">
                4
              </span>
              <p className="leading-relaxed text-slate-200">
                <span className="font-semibold text-white">We confirm and complete your
                request</span> within the time limits required by UK data protection law.
              </p>
            </li>
          </ol>

          <a
            href={mailto}
            className="mt-7 inline-flex items-center justify-center rounded-lg bg-teal-600 px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-teal-500"
          >
            Email admin@narratoria.com
          </a>

          <p className="mt-4 text-sm text-slate-400">
            This process applies whether you want your entire account deleted or only
            specific data removed — just tell us which in your email.
          </p>
        </section>

        {/* ── What is deleted vs retained ── */}
        <article
          className="prose prose-invert prose-slate mt-12 max-w-none
            prose-headings:font-semibold prose-headings:text-white
            prose-h2:mt-10 prose-h2:text-2xl
            prose-p:leading-relaxed prose-li:leading-relaxed
            prose-a:text-teal-400 hover:prose-a:text-teal-300
            prose-strong:text-white"
        >
          <h2>What we delete</h2>
          <p>
            When you ask us to delete your account, we remove your{' '}
            <strong>account information</strong> — your name and email address — and the{' '}
            <strong>account-linked data</strong> we hold about you, such as your audiobook
            rental and listening records (your rentals, whether you have downloaded a title,
            and your listening progress). Audio you have downloaded to your own device is
            removed when you delete it or uninstall the app.
          </p>

          <h2>What we keep, and why</h2>
          <p>
            We keep <strong>transaction and financial records</strong> — for example, records
            of rentals and purchases — for <strong>six years</strong>, to meet our legal and
            accounting obligations in the UK. We cannot delete these earlier, but they are
            kept only for that purpose and are not used for anything else.
          </p>

          <h2>Questions</h2>
          <p>
            If you have any questions about deleting your account or your data, contact us at{' '}
            <a href="mailto:admin@narratoria.com">admin@narratoria.com</a>. You can also read
            how we handle your personal data in our{' '}
            <a href="/privacy">Privacy Policy</a>.
          </p>
        </article>

        <footer className="mt-14 border-t border-slate-800 pt-6 text-center text-sm text-slate-500">
          Narratoria Ltd, registered in Scotland (company number SC880318).
        </footer>
      </div>
    </div>
  );
}
